import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { createColumnHelper } from "@tanstack/react-table";
import { useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { Icon } from "@iconify/react";
import { debounce } from "lodash";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";


const UsersTable = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sorting, setSorting] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [modifiedFields, setModifiedFields] = useState({});
    const [activeTab, setActiveTab] = useState('edit');

    const auth = useAuthHeader();

    const validateForm = () => {
        if (!name || name.trim().length === 0) {
            setError("Name is required");
            return false;
        }

        if (!email || !email.includes('@')) {
            setError("Please enter a valid email address");
            return false;
        }

        // Only validate password for new users
        if (!isEditMode) {
            const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).+$/;
            
            if (!passwordRegex.test(password)) {
                setError("Password must contain at least 1 letter and 1 number");
                return false;
            }

            if (password.length < 8) {
                setError("Password must be at least 8 characters long");
                return false;
            }
        }

        return true;
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError("");

        if (!validateForm()) {
            return;
        }

        try {
            if (isEditMode) {
                // Create update payload with only modified fields
                const updateData = {};
                if (modifiedFields.name) {
                    updateData.name = name;
                }
                if (modifiedFields.email) {
                    updateData.email = email;
                }

                // Only make the request if there are changes
                if (Object.keys(updateData).length > 0) {
                    const response = await axios.patch(
                        `${process.env.REACT_APP_BACKEND_URL}/users/${editingUser.id}`,
                        updateData,
                        { headers: { Authorization: auth } }
                    );

                    if (response) {
                        setIsModalOpen(false);
                        fetchData();
                        resetForm();
                    }
                } else {
                    setIsModalOpen(false);
                    resetForm();
                }
            } else {
                // Handle create (existing code)
                const response = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/auth/register`,
                    { email, password, name },
                    { headers: { Authorization: auth } }
                );

                if (response) {
                    setIsModalOpen(false);
                    fetchData();
                    resetForm();
                }
            }
        } catch (err) {
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError("An error occurred. Please try again.");
            }
            console.log("err", err);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/users/${id}`,
                { headers: { Authorization: auth } }
            );
            // Refresh the table after successful deletion
            fetchData();
        } catch (error) {
            console.error("Error deleting user:", error);
            // Show error message to user
            alert("Cannot delete this user because they have associated records in the system.");
        }
    };

    const handleEdit = (user) => {
        setIsEditMode(true);
        setEditingUser(user);
        setEmail(user.email);
        setName(user.name);
        setIsModalOpen(true);
        setModifiedFields({});
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (isEditMode) {
            setModifiedFields(prev => ({
                ...prev,
                email: e.target.value !== editingUser.email
            }));
        }
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
        if (isEditMode) {
            setModifiedFields(prev => ({
                ...prev,
                name: e.target.value !== editingUser.name
            }));
        }
    };

    const resetForm = () => {
        setEmail("");
        setName("");
        setPassword("");
        setError("");
        setIsEditMode(false);
        setEditingUser(null);
        setModifiedFields({});
        setActiveTab('edit');
    };

    const fetchData = useCallback(
        debounce(() => {
            const fetchDataAsync = async () => {
                try {
                    const params = new URLSearchParams();

                    if (sorting.length > 0) {
                        params.append("sortBy", sorting[0].id);
                        params.append("sortType", sorting[0].desc ? "desc" : "asc");
                    }

                    params.append("page", currentPage.toString());
                    params.append("limit", perPage.toString());

                    const response = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/users?${params.toString()}`,
                        { headers: { Authorization: auth } }
                    );

                    // Filter out admin users from the response data
                    const filteredData = response.data.filter(user => user.role !== 'ADMIN');
                    
                    setData(filteredData);
                    setTotalPages(Math.ceil(filteredData.length / perPage));
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            };
            fetchDataAsync();
        }, 300),
        [sorting, currentPage, perPage, auth]
    );

    useEffect(() => {
        fetchData();
    }, [sorting, currentPage, perPage]);

    const columnHelper = createColumnHelper();

    const columns = [
        columnHelper.accessor("id", {
            header: ({ column }) => (
                <div className="cursor-pointer select-none flex items-center gap-1"
                    onClick={() => column.toggleSorting()}>
                    ID
                    {column.getIsSorted() === "asc" && <Icon icon="ri:sort-asc" />}
                    {column.getIsSorted() === "desc" && <Icon icon="ri:sort-desc" />}
                    {!column.getIsSorted() && <Icon icon="ri:sort-line" className="opacity-30" />}
                </div>
            )
        }),
        columnHelper.accessor("email", {
            header: ({ column }) => (
                <div className="cursor-pointer select-none flex items-center gap-1"
                    onClick={() => column.toggleSorting()}>
                    Email
                    {column.getIsSorted() === "asc" && <Icon icon="ri:sort-asc" />}
                    {column.getIsSorted() === "desc" && <Icon icon="ri:sort-desc" />}
                    {!column.getIsSorted() && <Icon icon="ri:sort-line" className="opacity-30" />}
                </div>
            )
        }),
        columnHelper.accessor("name", {
            header: ({ column }) => (
                <div className="cursor-pointer select-none flex items-center gap-1"
                    onClick={() => column.toggleSorting()}>
                    Name
                    {column.getIsSorted() === "asc" && <Icon icon="ri:sort-asc" />}
                    {column.getIsSorted() === "desc" && <Icon icon="ri:sort-desc" />}
                    {!column.getIsSorted() && <Icon icon="ri:sort-line" className="opacity-30" />}
                </div>
            )
        }),
        columnHelper.accessor("role", {
            header: ({ column }) => (
                <div className="cursor-pointer select-none flex items-center gap-1"
                    onClick={() => column.toggleSorting()}>
                    Role
                    {column.getIsSorted() === "asc" && <Icon icon="ri:sort-asc" />}
                    {column.getIsSorted() === "desc" && <Icon icon="ri:sort-desc" />}
                    {!column.getIsSorted() && <Icon icon="ri:sort-line" className="opacity-30" />}
                </div>
            )
        }),
        columnHelper.accessor("actions", {
            header: "Actions",
            cell: (info) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleEdit(info.row.original)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleDelete(info.row.original.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                    >
                        Delete
                    </button>
                </div>
            ),
        }),
    ];

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const PaginationControls = () => (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div className="flex items-center">
                <select
                    value={perPage}
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                    className="mr-4 rounded border-gray-300 text-sm"
                >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                </select>
                <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                </span>
            </div>
            <div className="flex justify-end gap-2">
                <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded bg-gray-100 text-gray-800 disabled:opacity-50"
                >
                    Previous
                </button>
                <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded bg-gray-100 text-gray-800 disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        
        if (password.length < 8 || !/^(?=.*[A-Za-z])(?=.*\d).+$/.test(password)) {
            setError("Password must contain at least 8 characters with 1 letter and 1 number");
            return;
        }

        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/users/${editingUser.id}/password`,
                { password },
                { headers: { Authorization: auth } }
            );
            setIsModalOpen(false);
            resetForm();
        } catch (err) {
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError("An error occurred while updating password.");
            }
            console.log("err", err);
        }
    };

    return (
        <div className="max-w-9xl mx-auto h-[calc(100vh-200px)] flex flex-col">
            <div className="flex justify-between items-center mb-6 pr-10">
                <h2 className="text-2xl font-semibold text-gray-800">Users</h2>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    Add Users
                </button>
            </div>
            
            <div className="flex-1 flex flex-col bg-white shadow-lg rounded-lg min-h-0">
                <div className="flex-1 overflow-auto min-h-0">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="sticky top-0 bg-white z-10">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} className="bg-gray-100 text-gray-700 text-center text-sm">
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className="px-4 py-3 font-medium border text-left whitespace-nowrap">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row, idx) => (
                                <tr key={row.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-4 text-sm text-gray-600 border text-start">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="border-t">
                    <PaginationControls />
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold">
                                {isEditMode ? "Edit User" : "Add New User"}
                            </h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    resetForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        {error && (
                            <div className="p-3 text-sm text-red-600 bg-red-100 rounded-md mb-4">
                                {error}
                            </div>
                        )}

                        {isEditMode && (
                            <div className="flex border-b mb-4">
                                <button
                                    className={`py-2 px-4 ${
                                        activeTab === 'edit'
                                            ? 'border-b-2 border-blue-500 text-blue-500'
                                            : 'text-gray-500'
                                    }`}
                                    onClick={() => setActiveTab('edit')}
                                >
                                    Edit User
                                </button>
                                <button
                                    className={`py-2 px-4 ${
                                        activeTab === 'password'
                                            ? 'border-b-2 border-blue-500 text-blue-500'
                                            : 'text-gray-500'
                                    }`}
                                    onClick={() => setActiveTab('password')}
                                >
                                    Update Password
                                </button>
                            </div>
                        )}

                        {(!isEditMode || (isEditMode && activeTab === 'edit')) && (
                            <form onSubmit={handleAddUser} className="space-y-4">
                                <div>
                                    <label className="flex text-sm font-medium text-gray-700">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        className="block w-full px-4 py-2 mt-1 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="flex text-sm font-medium text-gray-700">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={handleNameChange}
                                        className="block w-full px-4 py-2 mt-1 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                        required
                                    />
                                </div>

                                {!isEditMode && (
                                    <div>
                                        <label className="flex text-sm font-medium text-gray-700">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="block w-full px-4 py-2 mt-1 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                                            >
                                                {showPassword ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Password must contain at least 8 characters with 1 letter and 1 number
                                        </p>
                                    </div>
                                )}

                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600"
                                    >
                                        {isEditMode ? "Update User" : "Add User"}
                                    </button>
                                </div>
                            </form>
                        )}

                        {isEditMode && activeTab === 'password' && (
                            <form onSubmit={handlePasswordUpdate} className="space-y-4">
                                <div>
                                    <label className="flex text-sm font-medium text-gray-700">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="block w-full px-4 py-2 mt-1 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                                        >
                                            {showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Password must contain at least 8 characters with 1 letter and 1 number
                                    </p>
                                </div>

                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600"
                                    >
                                        Update Password
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersTable;